import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { IonicModule, NavController, PopoverController } from '@ionic/angular';
import * as _ from 'lodash';
import moment from 'moment';
import { NgxTippyModule } from 'ngx-tippy-wrapper';

import { AvatarModule } from 'src/app/components/avatar/avatar.module';
import { formatSlackContent, transformMessage } from 'src/app/utils/utils';
import { Conversation } from 'src/models';
import { SharedPipesModule } from 'src/pipes/shared-pipes.module';
import { AnalyticsService } from 'src/services/analytics.service';
import { ConversationsService } from 'src/services/conversations.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';

import { ActionDropdownComponent, ActionDropdownType } from '../action-dropdown/action-dropdown.component';
import { TextPopoverComponent } from '../text-popover/text-popover.component';

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule, AvatarModule, RouterModule, NgxTippyModule, SharedPipesModule],
  selector: 'app-conversation-list',
  templateUrl: './conversation-list.component.html',
  styleUrls: ['./conversation-list.component.scss'],
})
export class ConversationListComponent {
  constructor(
    private conversationsService: ConversationsService,
    private searchablePopoverService: SearchablePopoverService,
    private analyticsService: AnalyticsService,
    private popoverCtrl: PopoverController,
    private navCtrl: NavController,
    private route: ActivatedRoute,
  ) {}

  public selectedConversations: Partial<Conversation>[] = [];
  private selectedConversationMap: any = {};
  public totalCount: number;

  @Input() conversations: Partial<Conversation>[] = [];
  @Input() searchText: string;
  @Input() limit = 25;
  @Input() sortDesc = true;
  @Input() sortField = 'createdAt';
  @Input() whereClauses: any[] = [];
  @Input() disableAssignment: boolean;
  @Input() disableSelection: boolean;
  @Input() routerPath: string;
  @Input() viewTag: string; // any custom identifier
  @Input() dragEnabled: boolean;
  @Input() showSummary: boolean;
  @Input() isSidebarMode: boolean;
  @Input() routerDirection: 'forward' | 'root' = 'forward';
  @Input() showEmptyState: boolean;
  @Input() unlinkingEnabled: boolean;
  @Input() showTags: boolean;

  @Output() conversationSelected = new EventEmitter<{ conversation: Conversation; index: Number }>();
  @Output() conversationStartedDragging = new EventEmitter<Conversation>();
  @Output() conversationStoppedDragging = new EventEmitter<Conversation>();
  @Output() didUpdateConversationCount = new EventEmitter<any>();
  @Output() didUnlinkConversation = new EventEmitter<Conversation>();

  isLoading = false;
  hasMore = false;
  cursor: any;
  hasInitialized = false;
  select = [
    'id',
    'status',
    'channelId',
    'teamId',
    'communityId',
    'createdAt',
    'updatedAt',
    'lastActivityAt',
    'statusUpdatedAt',
    'friendlyId',
    'wasManuallyCreated',
    'category',
    'closedAt',
    'respondedAt',
    'responseTime',
    'responseTimeWorking',
    'resolutionTime',
    'resolutionTimeWorking',
    'priority',
    'notes',
    'wakeUpAt',
    'snoozedAt',
    'excludeAnalytics',
    'sourceType',
    'submitterUserId',
    'inboundEmailRuleId',
    'widgetId',
    'externalChatConnectionId',
    'assignedToUserId',
    'assignedToUser.id',
    'assignedToUser.name',
    'assignedToUser.email',
    'assignedToUser.photo',
    'assignedToUser.slackId',
    'tags.id',
    'tags.name',
    'initialMessageId',
    'initialMessage.ts',
    'initialMessage.sourceType',
    'initialMessage.timestamp',
    'initialMessage.userId',
    'initialMessage.subtype',
    'initialMessage.teamId',
    'initialMessage.botId',
    'initialMessage.botName',
    'initialMessage.parentId',
    'initialMessage.id',
    'initialMessage.content',
    'initialMessage.fullContent',
    'initialMessage.nlpResult',
    'initialMessage.blocks',
    'initialMessage.inbound',
    'latestMessage.ts',
    'latestMessage.threadTs',
    'latestMessage.isAutoresponse',
    'latestMessage.isPrivateExplanation',
    'latestTopLevelTs',
    'customer.id',
    'customer.name',
    'customer.tags.id',
    'customer.tags.name',
    'customer.slackChannel.name',
    'customer.slackTeams.icon',
    'summary',
    'triageMessages',
    'slackChannel.id',
    'slackChannel.name',
    'externalTasks.status',
    'externalTasks.name',
    'externalTasks.url',
    'submitterUser.name',
    'submitterUser.photo',
    'submitterUser.email',
    // We only include these for the export... should find a better way to export
    'ticketType.id',
    'ticketType.name',
    'ticketTypeFields',
    'account.id',
    'account.name',
    'title',
    'metadata',
    'subStatusId',
    'subStatus.key',
    'projectId',
  ];

  public selectConversations(conversations: Partial<Conversation>[]) {
    this.selectedConversations = conversations;
    this.selectedConversations.forEach((c, i) => {
      this.didSelectConversation(c as Conversation, i);
    });
  }

  transformConversation(conversation: Partial<Conversation>) {
    let tasks = 0;

    conversation.externalTasks?.forEach((task) => {
      if (!task.isCompleted) {
        tasks++;
      }
    });

    return {
      ...conversation,
      initialMessage: conversation.initialMessage ? transformMessage(conversation.initialMessage) : undefined,
      _externalTaskTodoCount: tasks,
    };
  }

  async fetchConversations(firstPage?: boolean) {
    const options: any = {
      limit: this.limit,
      order: [this.sortField],
      descending: this.sortDesc,
      cursor: this.cursor,
      search: this.searchText || undefined,
      select: this.select,
    };

    if (this.whereClauses.length) {
      options.where = this.whereClauses;
    }

    const result = await this.conversationsService.list(options, true);

    result.data = result.data.map((conversation) => this.transformConversation(conversation));

    this.cursor = result.cursors.next;
    this.hasMore = result.cursors.hasNext;
    this.totalCount = result.totalCount;

    if (firstPage) {
      this.conversations = [...result.data];
    } else {
      this.conversations.push(...result.data);
    }

    this.didUpdateConversationCount.next({ viewTag: this.viewTag, count: result.totalCount });
    this.hasInitialized = true;
  }

  async addConversation(conversation: Partial<Conversation>) {
    const index = this.conversations.findIndex((c) => c.id === conversation.id);

    if (conversation) {
      const transformed = this.transformConversation(conversation);

      if (index === -1) {
        this.conversations.unshift(transformed);
      } else {
        this.conversations[index] = transformed;
      }
    }

    return true;
  }

  removeConversation(conversation: Partial<Conversation>) {
    const index = this.conversations.findIndex((c) => c.id === conversation.id);

    if (index !== -1) {
      this.conversations.splice(index, 1);
    }
  }

  async loadConversations() {
    this.isLoading = true;

    this.cursor = undefined;
    await this.fetchConversations(true);

    this.isLoading = false;
  }

  didClickConversation(conversation: Conversation, index: number) {
    if (this.isSidebarMode) {
      this.selectedConversationMap = { [conversation.id]: conversation };
      this.conversationSelected.next({ conversation, index });
    }

    this.cacheConversation(conversation);
  }

  cacheConversation(conversation: Conversation) {
    this.conversationsService.cachedConversation = conversation;
  }

  async nextPage() {
    this.isLoading = true;

    await this.fetchConversations();

    this.isLoading = false;
  }

  async viewNotes(conversation: Conversation) {
    const popover = await this.popoverCtrl.create({
      component: TextPopoverComponent,
      componentProps: {
        text: conversation.notes,
        title: 'Note',
        callback: async (text: string) => {
          conversation.notes = text;
          await this.conversationsService.update(conversation.id, { notes: text });
        },
      },
      showBackdrop: false,
      event,
    });
    popover.present();
  }

  async changeAssignee(conversation: Conversation) {
    this.searchablePopoverService.users({
      event,
      selectedUserId: conversation.assignedToUserId,
      callback: async (id: string) => {
        this.analyticsService.trackEvent('Conversation List', 'Change Assignee');

        this.isLoading = true;
        await this.conversationsService.update(conversation.id, { assignedToUserId: id });
        this.loadConversations();
      },
    });
  }

  displayDate(conversation: Conversation) {
    const sortableFields = ['createdAt', 'lastActivityAt', 'updatedAt'];
    const date =
      sortableFields.includes(this.sortField) && conversation[this.sortField]
        ? conversation[this.sortField]
        : conversation.createdAt;
    return moment(date).from(moment());
  }

  didSelectConversation(conversation: Conversation, index: number) {
    if (!this.selectedConversationMap[conversation.id]) {
      this.selectedConversationMap[conversation.id] = conversation;
    } else {
      delete this.selectedConversationMap[conversation.id];
    }

    this.selectedConversations = _.values(this.selectedConversationMap);
    this.conversationSelected.next({ conversation, index });
  }

  clearSelectedConversations() {
    this.selectedConversationMap = {};
    this.selectedConversations = [];
  }

  formatContent(conversation: Conversation, maxLength?: number) {
    const formattedContent = formatSlackContent(conversation.resolvedTitle);

    if (maxLength != null) {
      return formattedContent.length > maxLength ? formattedContent.substring(0, maxLength) + '...' : formattedContent;
    }

    return formattedContent;
  }

  isConversationSelected(id: string) {
    return !!this.selectedConversationMap[id];
  }

  onDragStart($event, conversation: Conversation) {
    this.conversationStartedDragging.next(conversation);
  }

  onDragEnd($event, conversation: Conversation) {
    this.conversationStoppedDragging.next(conversation);
  }

  conversationTrackBy(conversation: any) {
    return conversation.id;
  }

  tagTrackBy(tag: any) {
    return tag.id;
  }

  async viewTasks(conversation: Conversation) {
    const options: ActionDropdownType[] = conversation.externalTasks.map((t) => {
      return { label: `${t.isCompleted ? '[Done]' : '[Todo]'} ${t.name}`, icon: 'open-outline' };
    });

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: async (idx: number) => {
          popover.dismiss();

          window.open(conversation.externalTasks[idx].url, '_blank');
        },
      },
      showBackdrop: false,
      event,
    });
    popover.present();
  }

  changePriority(row: Conversation) {
    this.searchablePopoverService.priorities({
      event,
      selectedPriority: String(row.priority),
      callback: async (priority: string) => {
        this.analyticsService.trackEvent('Conversation List', 'Change Priority');

        row.priority = Number(priority);
        await this.conversationsService.update(row.id, { priority: Number(priority) });
      },
    });
  }

  changeStatus(row: Conversation) {
    this.searchablePopoverService.statuses({
      event,
      selectedStatusId: row.status,
      callback: async (status: string) => {
        this.analyticsService.trackEvent('Conversation List', 'Change Status');

        row.status = status;
        await this.conversationsService.update(row.id, { status });
        this.loadConversations();
      },
    });
  }

  public manuallyRouteToConversationAtIndex(index: number) {
    const conversation = this.conversations[index];

    if (conversation) {
      this.navCtrl.navigateRoot([this.routerPath, conversation.id], {
        relativeTo: this.route,
        queryParamsHandling: 'merge',
      });
      this.didClickConversation(conversation as Conversation, index);
    }
  }

  unlinkConversation(conversation: Conversation) {
    this.didUnlinkConversation.next(conversation);
  }
}
